/** @jsxImportSource react */
import { useMemo } from "react";
import type { MesaWithComparison } from "../lib/types";
import type { PoliticalForce, ExtractedActaData } from "../../shared/lib/actas";
import type { PoliticalGroup } from "../../shared/lib/electoral-results";
import {
  PARTY_DEFINITIONS,
  matchPartyByName,
} from "../../shared/lib/party-mappings";
import {
  resolveDistritoId,
  normalizeCircuitoId,
} from "../../shared/lib/district-mappings";
import { calculateMedian, calculateAverage } from "../lib/api";
import { SlopeChart } from "./SlopeChart";
import pbaCircuitData from "../../pba_2025.json";
import {
  getDistritoNameById,
  getCircuitoNameById,
  getSeccionNameByCircuito,
} from "../../shared/lib/catalog-lookup";

interface ComparisonTableProps {
  mesas: MesaWithComparison[];
  selectedMesa: MesaWithComparison | null;
  statMode: "median" | "average";
}

interface PartyRow {
  key: string;
  name: string;
  color: string;
  pct2023: number | null;
  pct2025: number | null;
  diff: number | null;
  pctCircuit2025: number | null;
  diffMesaCircuit: number | null;
}

function isBuenosAiresMesa(mesa: MesaWithComparison | null): boolean {
  if (!mesa?.data?.distrito) return false;
  return resolveDistritoId(mesa.data.distrito) === "02";
}

function findCircuitData(circuitoId: string | null | undefined) {
  if (!circuitoId) return null;
  const normalized = normalizeCircuitoId(circuitoId);
  return pbaCircuitData.find(
    (circuit) => circuit.circuit_metadata.circuitoId === normalized,
  );
}

function calculateCircuitTotal(circuitData: any): number {
  if (!circuitData) return 0;
  const positivos = circuitData.valoresTotalizadosPositivos.reduce(
    (acc: number, party: any) => acc + party.votos,
    0,
  );
  return positivos + circuitData.valoresTotalizadosOtros.votosEnBlanco;
}

function findPartyInCircuit(circuitData: any, partyKey: string) {
  if (!circuitData) return null;

  const partyInfo =
    PARTY_DEFINITIONS[partyKey as keyof typeof PARTY_DEFINITIONS];
  if (!partyInfo) return null;

  return circuitData.valoresTotalizadosPositivos.find((party: any) => {
    const matched = matchPartyByName(party.nombreAgrupacion);
    return matched?.key === partyKey;
  });
}

function calculateCircuitPercentage(
  circuitData: any,
  partyKey: string,
): number | null {
  const party = findPartyInCircuit(circuitData, partyKey);
  if (!party) return null;

  const total = calculateCircuitTotal(circuitData);
  if (total <= 0) return null;

  return (party.votos / total) * 100;
}

function getHistoricalData(data: ExtractedActaData | undefined) {
  return data?.historicalData?.generalCircuito;
}

function getTotal2023(data: ExtractedActaData | undefined): number {
  const historical = getHistoricalData(data);
  if (!historical) return 0;
  const positivos = historical.valoresTotalizadosPositivos.reduce(
    (acc, party) => acc + party.votos,
    0,
  );
  return positivos + historical.valoresTotalizadosOtros.votosEnBlanco;
}

function getTotal2025(data: ExtractedActaData | undefined): number {
  return data?.totalVotes ?? 0;
}

function findParty2023(
  data: ExtractedActaData | undefined,
  partyKey: string,
): PoliticalGroup | undefined {
  const historical = getHistoricalData(data);
  if (!historical) return undefined;

  const partyInfo =
    PARTY_DEFINITIONS[partyKey as keyof typeof PARTY_DEFINITIONS];
  if (!partyInfo) return undefined;

  // Find by matching normalized party names
  return historical.valoresTotalizadosPositivos.find((party) => {
    const matched = matchPartyByName(party.nombreAgrupacion);
    return matched?.key === partyKey;
  });
}

function findParty2025(
  data: ExtractedActaData | undefined,
  partyKey: string,
): PoliticalForce | undefined {
  if (!data?.politicalForces) return undefined;

  const partyInfo =
    PARTY_DEFINITIONS[partyKey as keyof typeof PARTY_DEFINITIONS];
  if (!partyInfo) return undefined;

  // Find by matching normalized party names
  return data.politicalForces.find((force) => {
    const matched = matchPartyByName(force.name);
    return matched?.key === partyKey;
  });
}

function calculatePercentage(
  votes: number | undefined,
  total: number,
): number | null {
  if (votes === undefined || votes === null || total <= 0) return null;
  return (votes / total) * 100;
}

export function mesaComparison(mesa: MesaWithComparison): Record<
  keyof typeof PARTY_DEFINITIONS,
  {
    pct2023: number | null;
    pct2025: number | null;
  }
> {
  const total2023 = getTotal2023(mesa.data);
  const total2025 = getTotal2025(mesa.data);

  const comparison = Object.fromEntries(
    Object.keys(PARTY_DEFINITIONS).map((key) => {
      const party2023 = findParty2023(mesa.data, key);
      const party2025 = findParty2025(mesa.data, key);
      return [
        key,
        {
          pct2023: calculatePercentage(party2023?.votos, total2023),
          pct2025: calculatePercentage(party2025?.votes, total2025),
        },
      ];
    }),
  ) as Record<
    keyof typeof PARTY_DEFINITIONS,
    {
      pct2023: number | null;
      pct2025: number | null;
    }
  >;

  return comparison;
}

function buildRowsForMesa(mesa: MesaWithComparison): PartyRow[] {
  const comparison = mesaComparison(mesa);

  // Check if this is a Buenos Aires mesa and get circuit data
  const isBuenosAires = isBuenosAiresMesa(mesa);
  const circuitData = isBuenosAires
    ? findCircuitData(mesa.data?.circuito)
    : null;

  return Object.entries(comparison).map(([key, value]) => {
    let pctCircuit2025: number | null = null;
    let diffMesaCircuit: number | null = null;

    if (circuitData) {
      pctCircuit2025 = calculateCircuitPercentage(circuitData, key);
      if (pctCircuit2025 !== null && value.pct2025 !== null) {
        diffMesaCircuit = value.pct2025 - pctCircuit2025;
      }
    }

    return {
      key: key,
      name: PARTY_DEFINITIONS[key as keyof typeof PARTY_DEFINITIONS]!
        .displayName,
      color: PARTY_DEFINITIONS[key as keyof typeof PARTY_DEFINITIONS]!.color,
      pct2023: value.pct2023,
      pct2025: value.pct2025,
      diff:
        value.pct2025 !== null && value.pct2023 !== null
          ? value.pct2025 - value.pct2023
          : null,
      pctCircuit2025,
      diffMesaCircuit,
    };
  });
}

export function mesaAggregateComparison(
  mesas: MesaWithComparison[],
  statMode: "median" | "average",
): Record<
  keyof typeof PARTY_DEFINITIONS,
  {
    pct2023: number | null;
    pct2025: number | null;
    diff: number | null;
  }
> {
  const comparisons = mesas.map((mesa) => mesaComparison(mesa));
  const aggregator = statMode === "median" ? calculateMedian : calculateAverage;
  let aggregated: Record<
    keyof typeof PARTY_DEFINITIONS,
    {
      pct2023: number | null;
      pct2025: number | null;
      diff: number | null;
    }
  > = {
    lla: {
      pct2023: null,
      pct2025: null,
      diff: null,
    },
    jxc: {
      pct2023: null,
      pct2025: null,
      diff: null,
    },
    fp: {
      pct2023: null,
      pct2025: null,
      diff: null,
    },
    provincias_unidas: {
      pct2023: null,
      pct2025: null,
      diff: null,
    },
  };
  Object.entries(PARTY_DEFINITIONS).forEach(([key, partyDef]) => {
    const pct2023Values: number[] = [];
    const pct2025Values: number[] = [];
    comparisons.forEach((comparison) => {
      const pct2023 = comparison[key as keyof typeof PARTY_DEFINITIONS].pct2023;
      const pct2025 = comparison[key as keyof typeof PARTY_DEFINITIONS].pct2025;
      if (key === "provincias_unidas") return;
      if (pct2023 !== null && pct2025 !== null) {
        pct2023Values.push(pct2023);
        pct2025Values.push(pct2025);
      } else if (key === "jxc" && pct2023 !== null) {
        pct2023Values.push(pct2023); // jxc no esta en 2025
      }
    });

    const pct2023Agg = pct2023Values.length ? aggregator(pct2023Values) : null;
    const pct2025Agg = pct2025Values.length ? aggregator(pct2025Values) : null;
    const diffAgg =
      pct2025Agg !== null && pct2023Agg !== null
        ? pct2025Agg - pct2023Agg
        : null;

    aggregated[key as keyof typeof PARTY_DEFINITIONS] = {
      pct2023: pct2023Agg,
      pct2025: pct2025Agg,
      diff: diffAgg,
    };
  });
  return aggregated;
}

function aggregateRows(
  mesas: MesaWithComparison[],
  statMode: "median" | "average",
): PartyRow[] {
  const comparison = mesaAggregateComparison(mesas, statMode);

  return Object.entries(PARTY_DEFINITIONS).map(([key, partyDef]) => {
    return {
      key: key,
      name: partyDef.displayName,
      color: partyDef.color,
      pct2023: comparison[key as keyof typeof PARTY_DEFINITIONS].pct2023,
      pct2025: comparison[key as keyof typeof PARTY_DEFINITIONS].pct2025,
      diff: comparison[key as keyof typeof PARTY_DEFINITIONS].diff,
      pctCircuit2025: null, // Not shown in aggregate view
      diffMesaCircuit: null, // Not shown in aggregate view
    };
  });
}

function formatPct(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "N/A";
  return `${value.toFixed(1)}%`;
}

function formatDiff(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "N/A";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}pp`;
}

function getDiffColor(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "text-slate-400";
  if (value > 0) return "text-green-700 font-semibold";
  if (value < 0) return "text-red-600 font-semibold";
  return "text-slate-400";
}

export function ComparisonTable({
  mesas,
  selectedMesa,
  statMode,
}: ComparisonTableProps) {
  const hasSelection = Boolean(selectedMesa);

  const rows = useMemo<PartyRow[]>(() => {
    if (selectedMesa) {
      return buildRowsForMesa(selectedMesa);
    }
    return aggregateRows(mesas, statMode);
  }, [selectedMesa, mesas, statMode]);

  // Check if we have circuit data to display
  const hasCircuitData = rows.some((row) => row.pctCircuit2025 !== null);

  if (!hasSelection && mesas.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-500">
        No hay datos para comparar.
      </div>
    );
  }

  const headerLabel = hasSelection
    ? `Mesa ${selectedMesa?.data.mesaNumber ?? "?"}`
    : `Datos agregados de ${mesas.length} mesas`;

  const distritoId = hasSelection
    ? resolveDistritoId(selectedMesa?.data.distrito)
    : null;
  const distritoName = hasSelection ? getDistritoNameById(distritoId) : "";
  const circuitoName =
    hasSelection && selectedMesa?.data.circuito
      ? getCircuitoNameById(distritoId, selectedMesa.data.circuito)
      : "";
  const seccionName =
    hasSelection && selectedMesa?.data.circuito
      ? getSeccionNameByCircuito(distritoId, selectedMesa.data.circuito)
      : "";

  const headerContextLines = hasSelection
    ? [seccionName, distritoName || "Sin distrito", circuitoName].filter(
        Boolean,
      )
    : [];

  return (
    <div className="flex h-full flex-col overflow-hidden items-center">
      {/* Top section: Header text + Table side-by-side */}
      <div className="flex gap-4 shrink-0 mb-4 px-3 overflow-hidden mt-2">
        {/* Header text on the left */}
        <div className="flex flex-col justify-center items-start text-2xl w-sm">
          <p className="font-semibold text-slate-800 break-words">
            {headerLabel}
          </p>
          {headerContextLines.map((line, index) => (
            <p key={index} className="text-slate-500 break-words">
              {line}
            </p>
          ))}
          {!hasSelection && (
            <span className="rounded border border-slate-200 bg-white px-2 py-1 font-medium text-slate-600 mt-2">
              {statMode === "median" ? "Mediana" : "Promedio"}
            </span>
          )}
        </div>

        {/* Table on the right */}
        <div className="overflow-hidden rounded-lg border border-slate-200 shrink-0">
          <table className="border-collapse text-xl">
            <thead>
              <tr>
                <th className="bg-neutral-500 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-white">
                  Partido
                </th>
                <th className="bg-neutral-500 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-white">
                  {hasSelection ? "2023 % (Circuito)" : "2023 %"}
                </th>
                {hasCircuitData && (
                  <th className="bg-neutral-500 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-white">
                    PBA 2025
                  </th>
                )}
                <th className="bg-neutral-500 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-white">
                  {hasSelection ? "Hoy %" : "Hoy"}
                </th>
                <th className="bg-neutral-500 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-white">
                  Dif vs 2023
                </th>
                {hasCircuitData && (
                  <th className="bg-neutral-500 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-white">
                    Dif vs Sep25
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((party, index) => (
                <tr
                  key={party.name}
                  className={`transition hover:bg-slate-50 ${
                    index !== rows.length - 1 ? "border-b border-slate-100" : ""
                  }`}
                  style={{
                    backgroundColor: party.color + "55",
                  }}
                >
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {party.name}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {formatPct(party.pct2023)}
                  </td>
                  {hasCircuitData && (
                    <td className="px-3 py-2 text-right font-semibold">
                      {formatPct(party.pctCircuit2025)}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right font-semibold">
                    {formatPct(party.pct2025)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${getDiffColor(party.diff)}`}
                  >
                    {formatDiff(party.diff)}
                  </td>
                  {hasCircuitData && (
                    <>
                      <td
                        className={`px-3 py-2 text-right ${getDiffColor(party.diffMesaCircuit)}`}
                      >
                        {formatDiff(party.diffMesaCircuit)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Chart spans full width below */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <SlopeChart mesas={mesas} statMode={statMode} />
      </div>
    </div>
  );
}
